import {AppRoute} from '../models/AppRoute';
import {ApplyOfferErrorCode, ApplyOfferErrorResponse, RecoveryAction} from '../transport/types';

/**
 * Frontend message overrides for brand-specific or marketing-approved copy.
 *
 * Use this to override server error messages when:
 * - Marketing requires specific wording
 * - Brand-specific terminology is needed
 * - Server messages are too technical for end users
 *
 * Example:
 *   OFFER_EXPIRED: 'This exclusive offer has ended. Check back for new deals!',
 */
const MESSAGE_OVERRIDES: Partial<Record<ApplyOfferErrorCode, string>> = {
  // Add overrides as needed, e.g.:
  // OFFER_EXPIRED: 'This offer has ended',
};

export interface ApplyOfferError {
  code: ApplyOfferErrorCode;
  message: string;
  recoverable: boolean;
  recoveryAction: RecoveryAction | null;
  details?: Record<string, unknown>;
}

export function parseApplyOfferError(response: unknown): ApplyOfferError {
  const err = (response as {error?: ApplyOfferErrorResponse['error']})?.error;
  const code: ApplyOfferErrorCode = err?.code || 'INTERNAL_ERROR';

  return {
    code,
    message: MESSAGE_OVERRIDES[code] || err?.message || 'Something went wrong',
    recoverable: err?.recoverable ?? false,
    recoveryAction: err?.recovery_action ?? null,
    details: err?.details,
  };
}

export function shouldAutoRetry(error: ApplyOfferError): boolean {
  return error.recoveryAction === 'retry';
}

const RECOVERY_ROUTES: Record<Exclude<RecoveryAction, 'retry'>, AppRoute> = {
  login: AppRoute.ACCOUNT_LOGIN,
  add_to_cart: AppRoute.SHOP,
  select_store: AppRoute.ROOT_STORE_SELECTION_FLOW,
  link_rewards: AppRoute.REWARDS,
};

const RECOVERY_LABELS: Record<Exclude<RecoveryAction, 'retry'>, string> = {
  login: 'Log In',
  add_to_cart: 'Browse Products',
  select_store: 'Select Store',
  link_rewards: 'Link Rewards Card',
};

export function getRecoveryRoute(action: RecoveryAction | null): AppRoute | null {
  if (!action || action === 'retry') return null;
  return RECOVERY_ROUTES[action];
}

export function getRecoveryButtonLabel(action: RecoveryAction | null): string | null {
  if (!action || action === 'retry') return null;
  return RECOVERY_LABELS[action];
}
